// Pembayaran dengan DP reservasi di kasir web (cafe): DP yang sudah diterima dipotong dari tagihan, sisanya dibayar tunai atau non-tunai.
// Aturan SAMA dengan server (backend controller/transaksiCabangController.js, bagian reservasiUuid): DP terpakai = min(DP, tagihan),
// tercatat sebagai bagian non-tunai "DP Reservasi <kode>". Sisa boleh tunai, non-tunai, atau QRIS (Midtrans, ditagih sebesar sisa; DP ditahan
// sampai QRIS lunas). Pembayaran bagi tidak digabung dengan DP reservasi.
import { barisBagi } from './bagiBayar';
import { semuaMejaReservasi } from './reservasi';

const TOLERANSI = 0.01;
const bulat2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

export const reservasiBayarAwal = () => ({ uuid: '', metode: 'cash', cash: '', nonCashUuid: '' });

/** Reservasi aktif yang punya DP diterima (siap dipotong di pembayaran), urut menurut jam. */
export const reservasiBerdp = (daftar) => (daftar || [])
  .filter((r) => ['dikonfirmasi', 'datang'].includes(r.status) && r.dp?.status === 'diterima')
  .sort((a, b) => String(a.mulai).localeCompare(String(b.mulai)));

/**
 * Reservasi yang DP-nya otomatis dipilih saat dialog pembayaran dibuka: reservasi ber-DP di meja yang dipilih kasir
 * (tamu yang sudah datang didahulukan, lalu yang paling awal). null bila meja belum dipilih atau tidak ada yang cocok.
 */
export const reservasiUntukMeja = (daftar, tableId) => {
  if (tableId === null || tableId === undefined || tableId === '') return null;
  const cocok = reservasiBerdp(daftar).filter((r) => semuaMejaReservasi(r).includes(Number(tableId)));
  if (!cocok.length) return null;
  return cocok.find((r) => r.status === 'datang') || cocok[0];
};

/**
 * Hitung & periksa pembayaran dengan DP reservasi.
 * @param total tagihan akhir (setelah promo & PPN)
 * @param st { uuid, metode: 'cash'|'noncash'|'qris', cash, nonCashUuid }
 * @param daftar reservasi (bentuk API) yang punya DP diterima
 * @param metodeNonTunai [{ uuid, name }]
 * @returns { sah, galat, r, dpPakai, sisa, sisaDpKembali, lunasDenganDp, kembalian, uangDiterima, payload, baris }
 */
export const hitungReservasiBayar = (total, st, daftar, metodeNonTunai) => {
  const tagihan = bulat2(total);
  const r = (daftar || []).find((x) => x.uuid === st?.uuid);
  if (!r) return { sah: false, galat: 'Pilih reservasi yang DP-nya dipakai' };
  if (tagihan <= 0) return { sah: false, galat: 'Keranjang kosong', r };
  const dpNominal = bulat2(r.dp?.nominal);
  const dpPakai = bulat2(Math.min(dpNominal, tagihan));
  const sisa = bulat2(tagihan - dpPakai);
  const dasar = { r, dpPakai, sisa, sisaDpKembali: bulat2(dpNominal - dpPakai) };
  const bagianDp = { metode: 'noncash', nonCashMethodName: `DP Reservasi ${r.kode}`, nominal: dpPakai, diterima: dpPakai };

  if (sisa <= TOLERANSI) {
    return {
      ...dasar, sah: true, galat: null, lunasDenganDp: true, kembalian: 0, uangDiterima: 0,
      payload: { reservasiUuid: r.uuid, pembayaran: 'cash', uangCustomer: 0 },
      baris: barisBagi([bagianDp], tagihan),
    };
  }
  if (st.metode === 'qris') {
    return {
      ...dasar, sah: true, galat: null, lunasDenganDp: false, kembalian: 0, uangDiterima: 0,
      payload: { reservasiUuid: r.uuid, pembayaran: 'qris' },
      baris: barisBagi([bagianDp, { metode: 'qris', nonCashMethodName: 'QRIS', nominal: sisa, diterima: sisa }], tagihan),
    };
  }
  if (st.metode === 'noncash') {
    const m = (metodeNonTunai || []).find((x) => x.uuid === st.nonCashUuid);
    if (!m) return { ...dasar, sah: false, galat: 'Pilih metode non-tunai untuk sisa tagihan', lunasDenganDp: false };
    return {
      ...dasar, sah: true, galat: null, lunasDenganDp: false, kembalian: 0, uangDiterima: 0,
      payload: { reservasiUuid: r.uuid, pembayaran: 'noncash', nonCashMethodName: m.name },
      baris: barisBagi([bagianDp, { metode: 'noncash', nonCashMethodName: m.name, nominal: sisa, diterima: sisa }], tagihan),
    };
  }
  const cash = bulat2(st.cash);
  if (cash + TOLERANSI < sisa) return { ...dasar, sah: false, galat: `Uang tunai kurang Rp ${Math.round(sisa - cash).toLocaleString('id-ID')}`, lunasDenganDp: false, kembalian: 0, uangDiterima: cash };
  return {
    ...dasar, sah: true, galat: null, lunasDenganDp: false, kembalian: bulat2(cash - sisa), uangDiterima: cash,
    payload: { reservasiUuid: r.uuid, pembayaran: 'cash', uangCustomer: cash },
    baris: barisBagi([bagianDp, { metode: 'cash', nominal: sisa, diterima: cash }], tagihan),
  };
};
